import { useEffect, useState } from "react";
import { Mail, CalendarClock, Send } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { adminApi, formatApiErrorDetail } from "../lib/adminApi";

const fmt = (iso) =>
  iso
    ? `${new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })} IST`
    : "—";

export default function AdminSettings() {
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/digest").then((res) => setDigest(res.data)).catch(() => {});
  }, []);

  const toggle = async () => {
    if (busy || !digest) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const res = await adminApi.patch("/admin/digest", { enabled: !digest.enabled });
      setDigest(res.data);
      setMessage(res.data.enabled ? "Weekly digest enabled." : "Weekly digest disabled. No scheduled emails will be sent.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const res = await adminApi.post("/admin/digest/send-test");
      setDigest(res.data);
      setMessage(`Test digest sent to ${res.data.recipient}. Check the inbox.`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title="Settings">
      <div className="max-w-3xl border border-border bg-white p-6 sm:p-8" data-testid="digest-settings-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-brand-graphite">
              <Mail className="h-5 w-5 text-brand-red" aria-hidden="true" /> Weekly Digest
            </h2>
            <p className="mt-1 max-w-lg text-sm leading-relaxed text-brand-grey">
              A weekly summary of new enquiries and job applications. Contains counts only — no
              customer or candidate personal information is ever included.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={digest?.enabled || false}
            onClick={toggle}
            disabled={busy || !digest}
            className={`relative h-7 w-14 shrink-0 rounded-full transition-colors ${
              digest?.enabled ? "bg-brand-red" : "bg-neutral-300"
            }`}
            data-testid="digest-toggle"
          >
            <span
              className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${
                digest?.enabled ? "left-8" : "left-1"
              }`}
            />
          </button>
        </div>

        {digest ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="border border-border bg-brand-surface p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Status</p>
              <p className="mt-1 text-sm font-semibold text-brand-graphite" data-testid="digest-status">
                {digest.enabled ? "Enabled" : "Disabled"}
              </p>
            </div>
            <div className="border border-border bg-brand-surface p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Recipient</p>
              <p className="mt-1 text-sm text-brand-graphite" data-testid="digest-recipient">{digest.recipient}</p>
            </div>
            <div className="border border-border bg-brand-surface p-4">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-grey">
                <CalendarClock className="h-3.5 w-3.5 text-brand-red" aria-hidden="true" /> Schedule
              </p>
              <p className="mt-1 text-sm text-brand-graphite" data-testid="digest-schedule">{digest.schedule}</p>
            </div>
            <div className="border border-border bg-brand-surface p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Next Scheduled</p>
              <p className="mt-1 text-sm text-brand-graphite" data-testid="digest-next-scheduled">
                {digest.enabled ? fmt(digest.nextScheduledAt) : "— (disabled)"}
              </p>
            </div>
            <div className="border border-border bg-brand-surface p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Last Sent</p>
              <p className="mt-1 text-sm text-brand-graphite" data-testid="digest-last-sent">{fmt(digest.lastSentAt)}</p>
            </div>
            <div className="border border-border bg-brand-surface p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Last Result</p>
              <p className="mt-1 text-sm text-brand-graphite" data-testid="digest-last-result">{digest.lastResult || "—"}</p>
            </div>
          </div>
        ) : (
          <p className="mt-6 text-sm text-brand-grey">Loading settings…</p>
        )}

        {message && (
          <p className="mt-5 border-l-2 border-brand-red bg-brand-surface p-3 text-sm text-brand-graphite" data-testid="digest-message">
            {message}
          </p>
        )}
        {error && (
          <p className="mt-5 border-l-2 border-brand-red bg-brand-surface p-3 text-sm text-brand-graphite" role="alert" data-testid="digest-error">
            {error}
          </p>
        )}

        <button
          type="button"
          className="btn-primary mt-6 flex items-center gap-2 disabled:opacity-50"
          onClick={sendTest}
          disabled={busy || !digest}
          data-testid="digest-send-test-button"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {busy ? "Working…" : "Send Test Digest"}
        </button>
      </div>
    </AdminLayout>
  );
}
